import React, { useMemo, useState } from 'react';
import { Btn, DangerBtn, KPI, KPIRow, Loading, Msg, Section, useAdminGet } from '../kit';
import { Modal } from '../email/emailKit';
import { SchedulerBanner } from './Overview';
import {
  Collapse, Dot, Fact, Facts, JsonBox, NoteLine, NumInput, Pill, type RecrawlCtx, S, Switch, UniLink, arr,
  fmtIST, fmtN, labelize, n0, num, obj, str, useAction,
} from './shared';

/**
 * Recrawl → Settings: every engine setting (app_settings "recrawl_settings",
 * validated with bounds by engine.save_settings), the scheduler with its kill
 * switches, the server facts the panel may show (never a secret's value) and
 * the master tools (backfill status, reconcile).
 *
 * Kill switches, strongest first: RECRAWL_AUTO unset on the server (the
 * scheduler thread never starts), the scheduler switch here (settings
 * .enabled), a university's own schedule / pause. Turning the scheduler or
 * auto-approve ON needs an explicit confirmation (the engine refuses the
 * change without confirm: true); turning them OFF is one click.
 */

type Kind = 'int' | 'float' | 'bool' | 'quiet' | 'steps' | 'email';
type Field = { key: string; label: string; help: string; kind: Kind; unit?: string; step?: number; locked?: boolean };

const GROUPS: { title: string; note?: string; fields: Field[] }[] = [
  {
    title: 'Scheduler',
    fields: [
      { key: 'enabled', kind: 'bool', label: 'Scheduler switch', help: 'Lets the scheduler start due universities by itself. Needs RECRAWL_AUTO=1 on the server too. Run now works either way.' },
      { key: 'quiet_hours_ist', kind: 'quiet', label: 'Quiet hours (IST)', help: 'No scheduled start inside this window (start hour → end hour, India time). The same hour twice = no quiet window.' },
      { key: 'default_every_days', kind: 'int', label: 'Default re-crawl interval', unit: 'days', help: 'For a university whose manifest / override gives no every_days.' },
      { key: 'sweep_every_min', kind: 'int', label: 'Sweep every', unit: 'min', help: 'How often the scheduler looks for a due university; at most one start per sweep.' },
      { key: 'startup_delay_min', kind: 'int', label: 'Start-up delay', unit: 'min', help: 'After an API restart, wait this long before the first sweep (a restart loop must not become a crawl loop).' },
      { key: 'retry_failed_after_hours', kind: 'int', label: 'Retry a failed run after', unit: 'h', help: 'A failed or interrupted scheduled run is tried again after this long.' },
      { key: 'max_consecutive_failures', kind: 'int', label: 'Give up after', unit: 'failures in a row', help: 'Then the university needs attention and is no longer scheduled.' },
    ],
  },
  {
    title: 'Approval rules',
    note: 'A run promotes without an admin only when auto-approve is on AND none of these trips (the first engine run of a university always waits).',
    fields: [
      { key: 'auto_approve', kind: 'bool', label: 'Auto-approve', help: 'Write the live site without an admin when no rule trips. Off = every run waits for approval.' },
      { key: 'approve_removals_min', kind: 'int', label: 'Removals — at least', unit: 'records', help: 'Waits when removals ≥ max(this, the % below of active).' },
      { key: 'approve_removals_pct', kind: 'int', label: 'Removals — % of active', unit: '%', help: 'See above.' },
      { key: 'approve_modified_pct', kind: 'int', label: 'Modified above', unit: '% of active', help: 'Waits when more than this share was modified.' },
      { key: 'approve_new_pct', kind: 'int', label: 'New above', unit: '% of active', help: 'Waits when more than this share is new.' },
      { key: 'remove_after_misses', kind: 'int', label: 'Propose a removal after', unit: 'missed runs', help: 'A technology must be absent from this many runs in a row before its removal is proposed (a single run is never trusted).' },
      { key: 'expected_min_ratio', kind: 'float', step: 0.05, label: 'Partial-scrape guard', unit: '× master', help: 'Held when mapped / master active falls below this (or above 3.0, or 0 records). Per-university override in its drawer.' },
    ],
  },
  {
    title: 'Moved technologies (URL changes)',
    note: 'A move keeps the UTECH id; it is never an addition or a removal. Content-changed moves also count toward the Modified rule, and a pair accepted below 0.5 similarity (a university override) always waits for an admin.',
    fields: [
      { key: 'moved_detection', kind: 'bool', label: 'Moved detection', help: 'Pairs a vanished URL with a new URL of the same university when the reference number is unique on both sides and the title or text agrees. The UTECH id, links and saved items stay. Off = a changed URL is one removal plus one addition. Always off for manual-file universities and those flagged no_reference_numbers / unstable_keys, unless the university’s drawer turns it on.' },
      { key: 'approve_moved_min', kind: 'int', label: 'Moved — at least', unit: 'records', help: 'Waits when moves ≥ max(this, the % below of master active + missing).' },
      { key: 'approve_moved_pct', kind: 'int', label: 'Moved — % of active', unit: '%', help: 'See above.' },
    ],
  },
  {
    title: 'Enrichment budget (Replicate)',
    fields: [
      { key: 'enrich_workers', kind: 'int', label: 'Workers', help: 'Parallel enrichment workers.' },
      { key: 'max_enrich_usd_per_run', kind: 'float', step: 0.5, label: 'Per run', unit: 'USD', help: 'Above it the run is held (enrich_budget) unless approved.' },
      { key: 'max_enrich_usd_per_month', kind: 'float', step: 1, label: 'Per month', unit: 'USD', help: 'The scheduler stops starting runs once the month is spent; per-run caps shrink to what is left.' },
      { key: 'est_usd_per_record', kind: 'float', step: 0.0001, label: 'Estimated cost per record', unit: 'USD', help: 'Used for the budget checks (~3–5 gpt-4o-mini calls per record).' },
    ],
  },
  {
    title: 'Worker resources (systemd unit)',
    fields: [
      { key: 'max_concurrent_runs', kind: 'int', label: 'Runs at a time', locked: true, help: 'One worker at a time across all universities (fixed by design).' },
      { key: 'cpu_quota_pct', kind: 'int', label: 'CPU quota', unit: '%', help: '100% = one of the API box’s 4 cores.' },
      { key: 'memory_max_mb', kind: 'int', label: 'Memory cap', unit: 'MB', help: 'systemd MemoryMax.' },
      { key: 'nice', kind: 'int', label: 'Nice', help: 'CPU priority (higher = gentler on the API).' },
      { key: 'io_weight', kind: 'int', label: 'IO weight', help: 'systemd IOWeight (lower = gentler on disk).' },
      { key: 'tasks_max', kind: 'int', label: 'Tasks max', help: 'Processes / threads the unit may start.' },
      { key: 'max_run_hours', kind: 'int', label: 'Stop a run after', unit: 'h', help: 'A worker running longer is stopped (SIGINT, then TERM / KILL).' },
    ],
  },
  {
    title: 'After a promote',
    fields: [
      { key: 'post_load_steps', kind: 'steps', label: 'Search layers to rebuild', help: 'search_indexes steps run in the API after a promote (retried while another index run is busy).' },
      { key: 'auto_resume_promote', kind: 'bool', label: 'Resume an interrupted promote', help: 'Re-queue a promote cut off by a restart (promote is idempotent; at most 3 tries).' },
      { key: 'horizon_for_new', kind: 'bool', label: 'Horizon for new technologies', help: 'Start a capped Horizon run after a promote that added technologies.' },
      { key: 'horizon_max_usd', kind: 'float', step: 0.5, label: 'Horizon cap', unit: 'USD', help: 'Cost cap of that Horizon run.' },
    ],
  },
  {
    title: 'Housekeeping',
    fields: [
      { key: 'keep_runs_per_university', kind: 'int', label: 'Runs kept in full', help: 'Per university on disk; older runs keep their logs and summary only.' },
      { key: 'keep_backups', kind: 'int', label: 'Pre-promote backups kept', help: 'Each promote first copies the corpus + master tables to disk; the newest this many runs keep theirs.' },
      { key: 'sample_urls', kind: 'int', label: 'Probe sample URLs', help: 'Live technology URLs the probe checks besides the portal.' },
      { key: 'notify_email', kind: 'email', label: 'Digest e-mail', help: 'Stored for a future digest — nothing sends e-mail yet.' },
    ],
  },
];
const ALL_FIELDS = GROUPS.flatMap(g => g.fields);
const EMAIL_RE = /^[^@\s,;]+@[^@\s,;]+\.[A-Za-z]{2,}$/;

const toDraft = (s: any) => {
  const o = obj(s);
  const d: Record<string, any> = {};
  ALL_FIELDS.forEach(f => {
    const v = o[f.key];
    if (f.kind === 'bool') d[f.key] = !!v;
    else if (f.kind === 'quiet') d[f.key] = arr(v).length === 2 ? [String(v[0]), String(v[1])] : ['', ''];
    else if (f.kind === 'steps') d[f.key] = arr(v).map(str);
    else if (f.kind === 'email') d[f.key] = str(v);
    else d[f.key] = num(v) === null ? '' : String(v);
  });
  return d;
};

const valueOf = (f: Field, v: any): any => {
  if (f.kind === 'bool' || f.kind === 'email') return v;
  if (f.kind === 'quiet') return [Number(v[0]), Number(v[1])];
  if (f.kind === 'steps') return v;
  return Number(v);
};

const SettingsTab: React.FC<{ ctx: RecrawlCtx }> = ({ ctx }) => {
  const [localTick, setLocalTick] = useState(0);
  const st = useAdminGet<any>(S, 'settings', {}, [ctx.tick, localTick]);
  const sch = useAdminGet<any>(S, 'scheduler', {}, [ctx.tick, localTick]);
  const bf = useAdminGet<any>(S, 'backfill_status', {}, [ctx.tick]);
  const reload = () => { setLocalTick(t => t + 1); ctx.reloadAll(); };
  const act = useAction(reload);

  const cur = obj(st.data?.settings);
  const defaults = obj(st.data?.defaults);
  const limits = obj(st.data?.limits);
  const choices = arr(st.data?.post_load_choices).map(str);
  const confirmOn = obj(st.data?.confirm_on);
  const env = obj(st.data?.env);
  const curKey = JSON.stringify(cur);

  const [draft, setDraft] = useState<Record<string, any>>({});
  const [draftFor, setDraftFor] = useState('');
  if (st.data && draftFor !== curKey) { setDraft(toDraft(cur)); setDraftFor(curKey); }
  const [confirm, setConfirm] = useState<{ body: Record<string, any>; keys: string[] } | null>(null);
  const [sweep, setSweep] = useState<any>(null);
  const [recon, setRecon] = useState<any>(null);

  /** A setting this API does not return yet (an engine older than the panel): shown, never validated or sent. */
  const unknown = (f: Field) => !!st.data && cur[f.key] === undefined && defaults[f.key] === undefined;

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    ALL_FIELDS.forEach(f => {
      const v = draft[f.key];
      if (v === undefined || unknown(f)) return;
      const lim = arr(limits[f.key]);
      if (f.kind === 'int' || f.kind === 'float') {
        const n = Number(v);
        if (str(v).trim() === '' || !Number.isFinite(n)) e[f.key] = 'a number';
        else if (f.kind === 'int' && !Number.isInteger(n)) e[f.key] = 'a whole number';
        else if (lim.length === 2 && (n < Number(lim[0]) || n > Number(lim[1]))) e[f.key] = `${lim[0]}–${lim[1]}`;
      } else if (f.kind === 'quiet') {
        const ok = arr(v).every(x => str(x).trim() !== '' && Number.isInteger(Number(x)) && Number(x) >= 0 && Number(x) <= 23);
        if (!ok) e[f.key] = 'two hours, 0–23';
      } else if (f.kind === 'email') {
        if (str(v).trim() && !EMAIL_RE.test(str(v).trim())) e[f.key] = 'an e-mail address or empty';
      } else if (f.kind === 'steps') {
        if (!arr(v).length) e[f.key] = 'at least one step';
      }
    });
    return e;
  }, [draft, limits, curKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  const changed = useMemo(() => ALL_FIELDS.filter(f => {
    if (draft[f.key] === undefined || f.locked || unknown(f)) return false;
    const a = valueOf(f, draft[f.key]);
    const b = cur[f.key];
    if (f.kind === 'int' || f.kind === 'float') return Number(b) !== a;
    return JSON.stringify(a) !== JSON.stringify(f.kind === 'email' ? str(b) : b);
  }), [draft, curKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  const send = (body: Record<string, any>, keys: string[]) => {
    const needs = keys.filter(k => body[k] === true && !cur[k] && confirmOn[k]);
    if (needs.length && !body.confirm) { setConfirm({ body, keys: needs }); return; }
    act.post('save', 'settings_set', body, () => `Saved: ${Object.keys(body).filter(k => k !== 'confirm').map(labelize).join(', ')}.`);
  };
  const save = () => {
    const body: Record<string, any> = {};
    changed.forEach(f => { body[f.key] = valueOf(f, draft[f.key]); });
    send(body, Object.keys(body));
  };

  const sc = obj(sch.data);
  const b = obj(bf.data);
  const bm = obj(b.master);
  const ev = obj(b.events);
  const drift = obj(b.drift);
  const drifting = arr(drift.drifting).slice().sort((x, y) => Math.abs(n0(y.diff)) - Math.abs(n0(x.diff)));
  const unmapped = arr(drift.unmapped_live);

  if (st.loading && !st.data) return <Loading label="Loading the recrawl settings…" />;
  if (st.error && !st.data) return <Msg kind="error">{st.error}</Msg>;

  const field = (f: Field) => {
    const v = draft[f.key];
    const err = errors[f.key];
    const lim = arr(limits[f.key]);
    const isChanged = changed.includes(f);
    const def = defaults[f.key];
    const defText = def === undefined ? '' : typeof def === 'boolean' ? (def ? 'on' : 'off') : Array.isArray(def) ? def.join(f.kind === 'quiet' ? '–' : ', ') : String(def === '' ? '(empty)' : def);
    return (
      <div key={f.key} className={`flex flex-col gap-1 rounded-lg border px-3 py-2 ${isChanged ? 'border-primary-500 bg-primary-100/30' : 'border-grayscale-300 bg-white'}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-grayscale-1300">{f.label}</span>
          <code className="text-[10.5px] text-grayscale-1100">{f.key}</code>
          {isChanged && <Pill tone="navy">changed</Pill>}
          {f.locked && <Pill tone="grey">fixed</Pill>}
          {unknown(f) && <Pill tone="amber" title="The API did not return this setting: update the API to change it">not on this server yet</Pill>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {f.kind === 'bool' && <Switch on={!!v} onChange={x => setDraft(d => ({ ...d, [f.key]: x }))} label={f.label} disabled={unknown(f)} />}
          {(f.kind === 'int' || f.kind === 'float') && (
            <>
              <NumInput value={str(v)} onChange={x => setDraft(d => ({ ...d, [f.key]: x }))} invalid={!!err} disabled={f.locked || unknown(f)}
                min={lim.length === 2 ? Number(lim[0]) : undefined} max={lim.length === 2 ? Number(lim[1]) : undefined}
                step={f.kind === 'float' ? f.step || 0.01 : 1} width="w-28" />
              {f.unit && <span className="text-xs text-grayscale-1100">{f.unit}</span>}
            </>
          )}
          {f.kind === 'quiet' && (
            <>
              <NumInput value={str(arr(v)[0])} onChange={x => setDraft(d => ({ ...d, [f.key]: [x, arr(d[f.key])[1]] }))} min={0} max={23} width="w-16" invalid={!!err} />
              <span className="text-xs">:00 →</span>
              <NumInput value={str(arr(v)[1])} onChange={x => setDraft(d => ({ ...d, [f.key]: [arr(d[f.key])[0], x] }))} min={0} max={23} width="w-16" invalid={!!err} />
              <span className="text-xs">:00 IST</span>
            </>
          )}
          {f.kind === 'email' && (
            <input type="email" value={str(v)} onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))} placeholder="(none)"
              className={`h-8 px-2 rounded-md border-[0.5px] bg-white text-sm w-64 ${err ? 'border-red-500' : 'border-primary-300'}`} />
          )}
          {f.kind === 'steps' && (
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {(choices.length ? choices : arr(v).map(str)).map(c => {
                const on = arr(v).includes(c);
                return (
                  <label key={c} className="inline-flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={on}
                      onChange={e => setDraft(d => {
                        const cur2 = arr(d[f.key]).map(str);
                        const next = e.target.checked ? [...cur2, c] : cur2.filter(x => x !== c);
                        const order = choices.length ? choices : next;
                        return { ...d, [f.key]: order.filter(x => next.includes(x)) };
                      })} /> {c}
                  </label>
                );
              })}
            </div>
          )}
          {err && <span className="text-xs text-red-700">must be {err}</span>}
        </div>
        <div className="text-[11.5px] text-grayscale-1100">
          {f.help}
          {defText && <span> · default {defText}</span>}
          {lim.length === 2 && f.kind !== 'bool' && <span> · bounds {lim[0]}–{lim[1]}</span>}
        </div>
      </div>
    );
  };

  const sw = obj(sweep);
  return (
    <>
      <SchedulerBanner scheduler={sc} env={env} />

      <Section title="Kill switches">
        <div className="grid gap-3 lg:grid-cols-3">
          <div className={`rounded-xl border p-3 flex flex-col gap-2 ${cur.enabled ? 'border-green-300 bg-green-50' : 'border-grayscale-300 bg-white'}`}>
            <div className="flex items-center gap-2">
              <Dot tone={cur.enabled ? 'green' : 'grey'} size={10} />
              <span className="font-bold text-grayscale-1300 font-grotesk">Scheduler switch: {cur.enabled ? 'ON' : 'OFF'}</span>
            </div>
            <div className="text-xs text-grayscale-1200">Starts due universities by itself (with RECRAWL_AUTO=1). Manual runs are not affected.</div>
            {cur.enabled ? (
              <Btn kind="danger" disabled={!!act.busy} onClick={() => act.post('off', 'settings_set', { enabled: false }, () => 'Scheduler switched off: nothing starts by itself.')}>
                Turn the scheduler off
              </Btn>
            ) : (
              <Btn kind="primary" disabled={!!act.busy} onClick={() => send({ enabled: true }, ['enabled'])}>Turn the scheduler on…</Btn>
            )}
          </div>
          <div className={`rounded-xl border p-3 flex flex-col gap-2 ${cur.auto_approve ? 'border-amber-300 bg-amber-50' : 'border-grayscale-300 bg-white'}`}>
            <div className="flex items-center gap-2">
              <Dot tone={cur.auto_approve ? 'amber' : 'grey'} size={10} />
              <span className="font-bold text-grayscale-1300 font-grotesk">Auto-approve: {cur.auto_approve ? 'ON' : 'OFF'}</span>
            </div>
            <div className="text-xs text-grayscale-1200">On: a run whose changes trip no approval rule writes the live site without an admin (after a backup).</div>
            {cur.auto_approve ? (
              <Btn kind="danger" disabled={!!act.busy} onClick={() => act.post('aoff', 'settings_set', { auto_approve: false }, () => 'Auto-approve off: every run waits for an admin.')}>
                Turn auto-approve off
              </Btn>
            ) : (
              <Btn disabled={!!act.busy} onClick={() => send({ auto_approve: true }, ['auto_approve'])}>Turn auto-approve on…</Btn>
            )}
          </div>
          <div className={`rounded-xl border p-3 flex flex-col gap-2 ${env.auto ? 'border-green-300 bg-green-50' : 'border-grayscale-300 bg-white'}`}>
            <div className="flex items-center gap-2">
              <Dot tone={env.auto ? 'green' : 'grey'} size={10} />
              <span className="font-bold text-grayscale-1300 font-grotesk">RECRAWL_AUTO: {env.auto ? '= 1' : 'not set'}</span>
            </div>
            <div className="text-xs text-grayscale-1200">
              The server-level switch, read once at start-up: set <code>RECRAWL_AUTO=1</code> in the API service environment and restart to allow
              the scheduler; unset it and restart to stop it whatever the switch says. Not changeable from here.
            </div>
            <div className="text-xs text-grayscale-1200">
              Scheduler thread: <b>{sc.running ? `running since ${fmtIST(sc.started_at)}` : 'not started'}</b>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Btn small disabled={!!act.busy} onClick={async () => {
            const r = await act.post('sweep', 'sweep_now', {}, x => {
              const s2 = obj(obj(x).sweep);
              return s2.started ? `Sweep started run #${obj(s2.started).id} (${str(obj(s2.started).code)}).` : `Sweep ran: ${str(s2.skipped || s2.error) || 'nothing due'}.`;
            });
            if (r) setSweep(obj(r).sweep);
          }}>{act.busy === 'sweep' ? 'Sweeping…' : 'Run a sweep now'}</Btn>
          <span className="text-xs text-grayscale-1100">Honours every gate (RECRAWL_AUTO, the switch, quiet hours, a run going, the budget); at most one start.</span>
        </div>
        {sweep && (
          <div className="text-xs text-grayscale-1200">
            Sweep at {fmtIST(sw.at)}: {sw.started ? <>started run #{str(obj(sw.started).id)}</> : str(sw.skipped || sw.error) || 'nothing started'}
            {arr(sw.due).length > 0 && <> · due: {arr(sw.due).map(str).join(', ')}</>}
          </div>
        )}
        <NoteLine note={act.note} onClose={() => act.setNote(null)} />
        {confirm && (
          <Modal title="Confirm" onClose={() => setConfirm(null)}
            footer={<><Btn onClick={() => setConfirm(null)}>Cancel</Btn>
              <Btn kind="primary" onClick={() => { const c = confirm; setConfirm(null); send({ ...c.body, confirm: true }, c.keys); }}>Yes, turn it on</Btn></>}>
            {confirm.keys.map(k => <p key={k}><b>{labelize(k)}:</b> {str(confirmOn[k])}.</p>)}
            {confirm.keys.includes('enabled') && !env.auto && (
              <p className="text-xs text-amber-800">RECRAWL_AUTO is not 1 on this server, so nothing will start by itself until it is.</p>
            )}
          </Modal>
        )}
      </Section>

      <Section title="Settings"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            {changed.length > 0 && <span className="text-xs text-grayscale-1100">{changed.length} unsaved change{changed.length === 1 ? '' : 's'}</span>}
            <Btn small disabled={!changed.length} onClick={() => setDraft(toDraft(cur))}>Discard</Btn>
            <Btn small kind="primary" disabled={!changed.length || Object.keys(errors).length > 0 || !!act.busy} onClick={save}>
              {act.busy === 'save' ? 'Saving…' : 'Save settings'}
            </Btn>
          </div>
        }>
        {Object.keys(errors).length > 0 && <Msg kind="error">Fix: {Object.keys(errors).map(labelize).join(', ')}.</Msg>}
        {GROUPS.map(g => (
          <div key={g.title} className="flex flex-col gap-2">
            <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 font-bold mt-1">{g.title}</div>
            {g.note && <div className="text-xs text-grayscale-1100 -mt-1">{g.note}</div>}
            <div className="grid gap-2 md:grid-cols-2">{g.fields.map(field)}</div>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1">
          <DangerBtn small onConfirm={() => act.post('reset', 'settings_reset', {}, () => 'Every setting is back to its default (scheduler and auto-approve off).')}>
            Reset all settings to defaults
          </DangerBtn>
          <span className="text-xs text-grayscale-1100">Defaults switch the scheduler and auto-approve off.</span>
        </div>
      </Section>

      <Section title="Server">
        <Facts>
          <Fact label="Launcher">{str(env.launcher) || '—'}{env.launcher === 'popen' ? ' (no systemd: Popen fallback)' : ''}</Fact>
          <Fact label="Worker venv">{env.venv_ok ? '✓ ' : '✗ '}{str(env.venv) || '—'}</Fact>
          <Fact label="Data folder">{env.data_dir_ok ? '✓ ' : '✗ '}{str(env.data_dir) || '—'}</Fact>
          <Fact label="Runs as">{str(env.run_user) || '—'}</Fact>
          <Fact label="Replicate token">{env.replicate_token ? '✓ present' : '✗ missing'}</Fact>
          <Fact label="Worker">{str(env.worker_module) || '—'}</Fact>
          <Fact label="Guide">{env.guide ? '✓ available' : '✗ missing'}</Fact>
          <Fact label="Month spend">{num(obj(sc.month_spend).spent_usd) !== null ? `$${n0(obj(sc.month_spend).spent_usd).toFixed(2)} of $${n0(obj(sc.month_spend).cap_usd).toFixed(2)}` : '—'}</Fact>
        </Facts>
      </Section>

      <Section title="Master copy and reconcile">
        {bf.error && <Msg kind="error">{bf.error}</Msg>}
        {bf.loading && !bf.data ? <Loading /> : (
          <>
            <KPIRow>
              <KPI label="Master rows" value={fmtN(bm.total)} hint={`${fmtN(bm.active)} active · ${fmtN(bm.missing)} missing · ${fmtN(bm.removed)} removed`} />
              <KPI label="Live rows" value={fmtN(b.live_total)} hint={`master listed ${fmtN(drift.master_listed_total)}`} />
              <KPI label="Events" value={fmtN(ev.total)} hint={Object.entries(obj(ev.by_source)).map(([k, v]) => `${k} ${fmtN(v)}`).join(' · ') || '—'} />
              <KPI label="History" value={b.backfill_applied ? 'backfilled' : 'not backfilled'}
                hint={b.first_event_at ? `${fmtIST(b.first_event_at, false)} → ${fmtIST(b.last_event_at, false)}` : 'no events yet'} />
            </KPIRow>
            {drifting.length > 0 && (
              <div className="flex flex-col gap-1">
                <div className="text-xs text-grayscale-1100">Universities whose live rows differ from master active + missing:</div>
                <div className="flex flex-col max-h-60 overflow-auto">
                  {drifting.map(r => (
                    <div key={str(r.code)} className="flex items-center gap-2 text-sm py-0.5">
                      <UniLink ctx={ctx} code={r.code} />
                      <span className="text-xs text-grayscale-1100 truncate flex-1">{str(r.name)}</span>
                      <span className="text-xs">live {fmtN(r.live)} · master {fmtN(r.listed)}</span>
                      <b className={n0(r.diff) ? 'text-red-700' : ''}>{n0(r.diff) > 0 ? '+' : ''}{fmtN(r.diff)}</b>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {unmapped.length > 0 && (
              <div className="text-xs text-amber-800">
                Live university names no manifest claims: {unmapped.map(u => `${str(u.university_name)} (${fmtN(u.live)})`).join(', ')}.
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <Btn small disabled={!!act.busy} onClick={async () => {
                const r = await act.post('recon', 'reconcile', { dry_run: true }, () => 'Dry run done — nothing was written.');
                if (r) setRecon(r);
              }}>{act.busy === 'recon' ? 'Checking…' : 'Reconcile — dry run'}</Btn>
              <DangerBtn small onConfirm={async () => {
                const r = await act.post('recon2', 'reconcile', { dry_run: false }, () => 'Reconciled: live rows the master did not know were recorded as manual loads.');
                if (r) setRecon(r);
              }}>Record live rows missing from the master</DangerBtn>
              <span className="text-xs text-grayscale-1100">Never writes the live tables; master rows the live site lacks are only reported.</span>
            </div>
            {recon && (
              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap gap-1.5 text-xs">
                  {Object.entries(obj(recon)).filter(([, v]) => v === null || typeof v !== 'object').map(([k, v]) => (
                    <span key={k} className="rounded-full bg-grayscale-200 px-2 py-0.5">{labelize(k)} <b>{typeof v === 'number' ? v.toLocaleString() : str(v)}</b></span>
                  ))}
                </div>
                <Collapse title="Full reconcile report"><JsonBox value={recon} maxH="22rem" /></Collapse>
              </div>
            )}
            {b.backfill_marker && <Collapse title="Backfill marker"><JsonBox value={b.backfill_marker} /></Collapse>}
          </>
        )}
      </Section>
      <div className="text-[11px] text-grayscale-1100">Every change made here is recorded in the admin audit log.</div>
    </>
  );
};

export default SettingsTab;
